/**
 * ELITE sticky chrome — announcement + header as one sticky system.
 * Lightweight scroll state (rAF). No ScrollTrigger.
 * Mobile menu: viewport-level overlay with scroll lock only while open.
 */
(function () {
  'use strict';

  const SCROLL_THRESHOLD = 50;
  const MENU_CLOSE_MS = 560;
  let mounted = false;
  let chromeEl = null;
  let rafId = 0;
  let lastY = 0;
  let lastDir = '';
  let entranceDone = false;
  let menuCloseTimer = 0;
  /** @type {Window | HTMLElement | null} */
  let scrollRoot = null;

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getScrollRoot() {
    const wrapper = document.querySelector('.page-wrapper');
    // Horizon scrolls .page-wrapper on desktop; document on smaller viewports
    if (wrapper && window.matchMedia('(min-width: 990px)').matches) return wrapper;
    return window;
  }

  function getScrollY() {
    const root = scrollRoot || getScrollRoot();
    if (root === window) return window.scrollY || window.pageYOffset || 0;
    return root.scrollTop || 0;
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function mountChrome() {
    const group = document.getElementById('header-group');
    if (!group) return null;

    const annSection = group.querySelector('.elite-announcement-section');
    const headerSection = group.querySelector('.elite-header-section');
    if (!annSection || !headerSection) return null;

    let chrome = group.querySelector('[data-elite-chrome]');
    if (!chrome) {
      chrome = document.createElement('div');
      chrome.className = 'elite-chrome';
      chrome.setAttribute('data-elite-chrome', '');
      group.insertBefore(chrome, annSection);
    }

    if (annSection.parentElement !== chrome) chrome.appendChild(annSection);
    if (headerSection.parentElement !== chrome) chrome.appendChild(headerSection);

    // Keep intro (if any) outside chrome, before it
    const intro = group.querySelector('.elite-intro-section, [data-elite-intro]')?.closest('.shopify-section');
    if (intro && intro.parentElement === group && chrome.previousElementSibling !== intro) {
      group.insertBefore(intro, chrome);
    }

    chromeEl = chrome;
    return chrome;
  }

  /**
   * Escape header stacking traps (backdrop-filter / transform).
   * Mount menu as a direct body child so position:fixed is viewport-true.
   */
  function mountMobileMenu(drawer) {
    if (!drawer) return;
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
  }

  function setMenuScrollLock(locked) {
    document.documentElement.classList.toggle('elite-menu-open', locked);
    document.body.classList.toggle('menu-open', locked);
    // Never leave inline overflow locks behind (previous homepage scroll bug)
    if (!locked) {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper instanceof HTMLElement) {
        wrapper.style.removeProperty('overflow');
      }
    }
  }

  function setScrolled(y) {
    if (!chromeEl) return;
    const scrolled = y > SCROLL_THRESHOLD;
    chromeEl.classList.toggle('is-scrolled', scrolled);
    const header = qs('[data-elite-header]', chromeEl);
    header?.classList.toggle('is-scrolled', scrolled);
  }

  function setDirection(y) {
    if (!chromeEl || reduced()) return;
    if (y <= SCROLL_THRESHOLD) {
      chromeEl.classList.remove('is-scrolling-down', 'is-scrolling-up');
      lastDir = '';
      return;
    }
    const delta = y - lastY;
    if (Math.abs(delta) < 2) return;
    const dir = delta > 0 ? 'down' : 'up';
    if (dir === lastDir) return;
    lastDir = dir;
    chromeEl.classList.toggle('is-scrolling-down', dir === 'down');
    chromeEl.classList.toggle('is-scrolling-up', dir === 'up');
  }

  function onScroll() {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      // Ignore scroll while menu covers the page
      if (document.documentElement.classList.contains('elite-menu-open')) return;
      const y = getScrollY();
      setScrolled(y);
      setDirection(y);
      lastY = y;
    });
  }

  function bindScroll() {
    const next = getScrollRoot();
    if (scrollRoot === next) return;
    if (scrollRoot) scrollRoot.removeEventListener('scroll', onScroll);
    scrollRoot = next;
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
  }

  function animateEntrance() {
    if (entranceDone) return;
    const ann = qs('[data-elite-announcement]');
    const header = qs('[data-elite-header]');
    if (!ann && !header) return;
    entranceDone = true;

    const revealStatic = () => {
      ann?.classList.remove('is-enter');
      header?.classList.remove('is-enter');
      ann?.classList.add('is-ready', 'is-visible');
      header?.classList.add('is-ready', 'is-visible');
    };

    if (reduced() || Boolean(window.Shopify?.designMode)) {
      revealStatic();
      return;
    }

    const run = () => {
      // Arm enter states only at animation start (avoids FOUC if JS delayed)
      ann?.classList.add('is-enter');
      header?.classList.add('is-enter');
      window.setTimeout(revealStatic, 2000);

      if (ann) {
        const text = qs('.elite-announcement__text', ann);
        void ann.offsetWidth;
        ann.style.transition = 'clip-path 580ms cubic-bezier(0.65, 0, 0.35, 1)';
        requestAnimationFrame(() => {
          ann.classList.remove('is-enter');
          ann.classList.add('is-visible');
        });

        if (text) {
          text.style.transition = 'opacity 420ms ease, transform 420ms ease';
          window.setTimeout(() => {
            text.style.opacity = '1';
            text.style.transform = 'translateY(0)';
          }, 120);
        }

        window.setTimeout(() => {
          ann.classList.add('is-ready');
          ann.style.transition = '';
          if (text) {
            text.style.transition = '';
            text.style.opacity = '';
            text.style.transform = '';
          }
        }, 700);
      }

      if (header) {
        const logo = qs('[data-elite-chrome-logo]', header);
        const links = qsa('[data-elite-chrome-nav]', header);
        const controls = qsa('[data-elite-chrome-control]', header);

        header.style.transition = 'opacity 600ms ease, transform 600ms ease';
        window.setTimeout(() => {
          header.classList.remove('is-enter');
          header.classList.add('is-visible');
        }, ann ? 280 : 40);

        if (logo) {
          logo.style.opacity = '0';
          logo.style.transform = 'translateY(-6px)';
          logo.style.transition = 'opacity 450ms ease, transform 450ms ease';
          window.setTimeout(() => {
            logo.style.opacity = '1';
            logo.style.transform = 'translateY(0)';
          }, ann ? 320 : 60);
        }

        links.forEach((link, i) => {
          link.style.opacity = '0';
          link.style.transform = 'translateY(-4px)';
          link.style.transition = 'opacity 400ms ease, transform 400ms ease';
          window.setTimeout(
            () => {
              link.style.opacity = '1';
              link.style.transform = 'translateY(0)';
            },
            (ann ? 380 : 100) + i * 50
          );
        });

        controls.forEach((el, i) => {
          el.style.opacity = '0';
          el.style.transform = 'translateY(-4px)';
          el.style.transition = 'opacity 400ms ease, transform 400ms ease';
          window.setTimeout(
            () => {
              el.style.opacity = '1';
              el.style.transform = 'translateY(0)';
            },
            (ann ? 480 : 180) + i * 40
          );
        });

        window.setTimeout(() => {
          header.classList.add('is-ready');
          header.style.transition = '';
          [logo, ...links, ...controls].forEach((el) => {
            if (!el) return;
            el.style.opacity = '';
            el.style.transform = '';
            el.style.transition = '';
          });
        }, 1200);
      }
    };

    if (document.querySelector('[data-elite-intro]') && !document.documentElement.classList.contains('elite-ready')) {
      document.addEventListener('elite:intro-complete', run, { once: true });
      window.setTimeout(run, 2800);
    } else {
      run();
    }
  }

  function bindMenu(header) {
    if (!header || header.dataset.eliteMenuBound === '1') return;
    header.dataset.eliteMenuBound = '1';

    const openBtn = qs('[data-elite-menu-open]', header);
    let drawer = qs('[data-elite-menu-drawer]', header) || qs('[data-elite-menu-drawer]');
    if (!openBtn || !drawer) return;

    mountMobileMenu(drawer);
    drawer = qs('[data-elite-menu-drawer]');
    const closeBtn = qs('[data-elite-menu-close]', drawer);

    const finishClose = () => {
      drawer.classList.remove('is-open', 'is-closing');
      drawer.hidden = true;
      chromeEl?.classList.remove('is-menu-open');
      setMenuScrollLock(false);
      openBtn.setAttribute('aria-expanded', 'false');
    };

    const setOpen = (open) => {
      if (menuCloseTimer) {
        window.clearTimeout(menuCloseTimer);
        menuCloseTimer = 0;
      }

      if (open) {
        drawer.hidden = false;
        drawer.classList.remove('is-closing');
        // Force reflow so clip-path transition always runs
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
        chromeEl?.classList.add('is-menu-open');
        openBtn.setAttribute('aria-expanded', 'true');
        setMenuScrollLock(true);
        closeBtn?.focus({ preventScroll: true });
        return;
      }

      if (!drawer.classList.contains('is-open') && !drawer.classList.contains('is-closing')) {
        finishClose();
        return;
      }

      drawer.classList.add('is-closing');
      drawer.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');

      const done = () => {
        menuCloseTimer = 0;
        finishClose();
      };

      if (reduced()) {
        done();
        return;
      }

      menuCloseTimer = window.setTimeout(done, MENU_CLOSE_MS);
    };

    const onOpen = (e) => {
      e.preventDefault();
      setOpen(true);
    };
    const onClose = (e) => {
      if (e) e.preventDefault();
      setOpen(false);
    };

    openBtn.addEventListener('click', onOpen);
    closeBtn?.addEventListener('click', onClose);
    drawer.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', () => setOpen(false));
    });

    if (document.documentElement.dataset.eliteMenuEscBound !== '1') {
      document.documentElement.dataset.eliteMenuEscBound = '1';
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openDrawer = qs('.elite-mobile-menu.is-open');
        if (!openDrawer) return;
        const btn = qs('[data-elite-menu-open]');
        openDrawer.classList.add('is-closing');
        openDrawer.classList.remove('is-open');
        btn?.setAttribute('aria-expanded', 'false');
        chromeEl?.classList.remove('is-menu-open');
        window.setTimeout(() => {
          if (!openDrawer.classList.contains('is-open')) {
            openDrawer.classList.remove('is-closing');
            openDrawer.hidden = true;
            setMenuScrollLock(false);
          }
        }, MENU_CLOSE_MS);
      });
    }
  }

  function bindBag(header) {
    const bag = qs('.elite-header__bag', header);
    if (!bag || bag.dataset.eliteBagBound === '1') return;
    bag.dataset.eliteBagBound = '1';

    bag.addEventListener('click', () => {
      if (reduced()) return;
      bag.classList.add('is-pressed');
      window.setTimeout(() => bag.classList.remove('is-pressed'), 160);
    });
  }

  function bindCartCount() {
    const badges = qsa('[data-elite-cart-count]');
    if (!badges.length || document.documentElement.dataset.eliteCartBound === '1') return;
    document.documentElement.dataset.eliteCartBound = '1';

    const update = (count) => {
      const n = Number(count) || 0;
      badges.forEach((badge) => {
        const prev = badge.textContent;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.classList.toggle('is-empty', n === 0);
        if (!reduced() && n > 0 && prev !== badge.textContent) {
          badge.classList.remove('is-pulse');
          void badge.offsetWidth;
          badge.classList.add('is-pulse');
          window.setTimeout(() => badge.classList.remove('is-pulse'), 220);
        }
      });
    };

    document.addEventListener('cart:update', (event) => {
      const d = event.detail || {};
      const count = d.resource?.item_count ?? d.cart?.item_count ?? d.itemCount;
      if (count != null) update(count);
    });
  }

  function initHeaderBits() {
    const header = qs('[data-elite-header]');
    if (!header) return;
    bindMenu(header);
    bindBag(header);
  }

  function boot() {
    document.documentElement.classList.add('elite-js');
    mountChrome();
    initHeaderBits();
    bindCartCount();
    bindScroll();
    const y = getScrollY();
    setScrolled(y);
    lastY = y;
    if (!mounted) {
      mounted = true;
      window.addEventListener(
        'resize',
        () => {
          bindScroll();
          onScroll();
          // Close mobile menu if viewport leaves mobile range
          if (window.matchMedia('(min-width: 990px)').matches) {
            const openMenu = qs('.elite-mobile-menu.is-open, .elite-mobile-menu.is-closing');
            if (openMenu) {
              openMenu.classList.remove('is-open', 'is-closing');
              openMenu.hidden = true;
              chromeEl?.classList.remove('is-menu-open');
              setMenuScrollLock(false);
              qs('[data-elite-menu-open]')?.setAttribute('aria-expanded', 'false');
            }
          }
        },
        { passive: true }
      );
    }
    animateEntrance();
  }

  function destroyChromeBindings() {
    const header = qs('[data-elite-header]');
    if (header) {
      header.dataset.eliteMenuBound = '0';
      header.dataset.eliteInitialized = 'false';
      const bag = qs('.elite-header__bag', header);
      if (bag) bag.dataset.eliteBagBound = '0';
    }
    const drawer = qs('[data-elite-menu-drawer]');
    if (drawer) {
      drawer.classList.remove('is-open', 'is-closing');
      drawer.hidden = true;
    }
    chromeEl?.classList.remove('is-menu-open');
    setMenuScrollLock(false);
    entranceDone = false;
  }

  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const related =
      event.target.classList.contains('elite-announcement-section') ||
      event.target.classList.contains('elite-header-section') ||
      event.target.querySelector('[data-elite-announcement], [data-elite-header]');
    if (!related) return;
    destroyChromeBindings();
    mountChrome();
    initHeaderBits();
    const ann = qs('[data-elite-announcement]');
    const header = qs('[data-elite-header]');
    ann?.classList.add('is-ready', 'is-visible');
    header?.classList.add('is-ready', 'is-visible');
    setScrolled(getScrollY());
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (
      event.target.classList.contains('elite-announcement-section') ||
      event.target.classList.contains('elite-header-section')
    ) {
      destroyChromeBindings();
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    destroyChromeBindings();
    window.setTimeout(boot, 30);
  });
})();
